import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Download,
  FileWarning,
  MonitorDown,
  ShieldCheck,
  Sparkles,
  Wand2,
} from "lucide-react";

const DOWNLOAD_URL = "/downloads/Wizard-RIP-Setup-1.0.0.exe";
const SUPPORT_EMAIL = "support@dtfwizardapp.com";

function BluePanel({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={[
        "relative overflow-hidden rounded-[28px] border border-dtf-accent/20",
        "bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.18),transparent_42%),linear-gradient(180deg,rgba(7,16,35,0.98)_0%,rgba(6,14,30,0.98)_100%)]",
        "shadow-[0_0_0_1px_rgba(245,158,11,0.05),0_0_34px_rgba(37,99,235,0.16)]",
        className,
      ].join(" ")}
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.08),transparent_30%,transparent_70%,rgba(37,99,235,0.06))]" />
      <div className="pointer-events-none absolute inset-0 rounded-[28px] ring-1 ring-inset ring-white/5" />
      {children}
    </div>
  );
}

function StepCard({ number, title, text }: { number: string; title: string; text: string }) {
  return (
    <div className="rounded-[24px] border border-white/10 bg-black/20 p-5 shadow-[0_0_18px_rgba(37,99,235,0.08)]">
      <div className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded-xl border border-dtf-blue/25 bg-dtf-blue/10 text-sm font-black text-dtf-blue">
        {number}
      </div>
      <h3 className="text-base font-extrabold text-white">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-dtf-text-muted">{text}</p>
    </div>
  );
}

function InfoRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm leading-6 text-white/90">
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-dtf-blue" />
      <div>{children}</div>
    </div>
  );
}

export default function SpotWizardPage() {
  const supportMailto = `mailto:${SUPPORT_EMAIL}?subject=DTF%20Spot%20Wizard%20Download%20Question`;

  return (
    <div className="min-h-screen bg-[#070b14] text-white">
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
        <BluePanel className="p-6 md:p-10">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_center,rgba(59,130,246,0.24),transparent_45%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_28%)]" />

          <div className="relative grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-dtf-blue">
                <Wand2 className="h-4 w-4" />
                Desktop Companion Tool
              </div>

              <h1 className="mt-5 text-4xl font-black tracking-tight text-white md:text-5xl">
                DTF Spot Wizard
              </h1>

              <p className="mt-4 max-w-3xl text-sm leading-7 text-dtf-text md:text-base">
                Download the Windows desktop tool for creating Maintop-ready CMYK + W1 TIFF files. This is for heavier spot-color output work that needs to run locally on your computer, not inside the browser.
              </p>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <a
                  href={DOWNLOAD_URL}
                  download
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-dtf-accent px-6 py-3 text-sm font-bold text-black shadow-[0_0_20px_rgba(245,158,11,0.25)] transition hover:opacity-90"
                >
                  <Download className="h-4 w-4" />
                  Download Windows App
                </a>

                <Link
                  to="/color-knockout"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-6 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:border-dtf-blue/30 hover:bg-black/30"
                >
                  Open Color Knockout First
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="grid gap-4">
              <div className="rounded-[28px] border border-dtf-blue/25 bg-[linear-gradient(180deg,rgba(37,99,235,0.12),rgba(0,0,0,0.20))] p-5 shadow-[0_0_26px_rgba(37,99,235,0.12)]">
                <div className="flex items-start gap-4">
                  <div className="rounded-2xl border border-dtf-blue/25 bg-dtf-blue/10 p-3">
                    <MonitorDown className="h-7 w-7 text-dtf-blue" />
                  </div>
                  <div>
                    <h2 className="text-xl font-extrabold text-white">Windows download</h2>
                    <p className="mt-2 text-sm leading-6 text-dtf-text-muted">
                      Works as a separate desktop app. Download it, install or run it, then use it alongside DTF Wizard.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-[28px] border border-amber-400/25 bg-[linear-gradient(180deg,rgba(245,158,11,0.12),rgba(0,0,0,0.18))] p-5 shadow-[0_0_26px_rgba(245,158,11,0.08)]">
                <div className="flex items-start gap-4">
                  <div className="rounded-2xl border border-amber-400/25 bg-amber-400/10 p-3">
                    <ShieldCheck className="h-7 w-7 text-amber-300" />
                  </div>
                  <div>
                    <h2 className="text-xl font-extrabold text-white">Microsoft security notice</h2>
                    <p className="mt-2 text-sm leading-6 text-white/85">
                      Windows may show a blue Microsoft Defender SmartScreen popup because this is a new small-batch tool and may not have publisher reputation yet. That warning is common for new independent Windows apps.
                    </p>
                    <p className="mt-2 text-sm leading-6 text-dtf-text-muted">
                      Only download this file from inside DTF Wizard or Apex Designs links. Never download random copies from other websites.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </BluePanel>

        <section className="mt-8 grid gap-6 lg:grid-cols-3">
          <StepCard
            number="1"
            title="Prepare artwork"
            text="Use your normal artwork workflow, or clean the background first with Color Knockout."
          />
          <StepCard
            number="2"
            title="Run Spot Wizard"
            text="Open the Windows tool and create the CMYK + W1 TIFF output for Maintop workflows."
          />
          <StepCard
            number="3"
            title="Print with correct RIP settings"
            text="Use the exported TIFF in Maintop and confirm your white settings before production."
          />
        </section>

        <section className="mt-8 grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
          <BluePanel className="p-6 md:p-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
              <Sparkles className="h-4 w-4" />
              What It Does
            </div>

            <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-white md:text-3xl">
              Built for W1 spot output workflows
            </h2>

            <div className="mt-5 grid gap-3">
              <InfoRow>Creates a dedicated W1-style output workflow for white ink preparation.</InfoRow>
              <InfoRow>Designed for users who need a local desktop tool instead of browser-only processing.</InfoRow>
              <InfoRow>Helps keep DTF Wizard as the workflow hub while the EXE handles heavier file work.</InfoRow>
              <InfoRow>Best for advanced users who understand Maintop, white channels, and RIP setup.</InfoRow>
            </div>
          </BluePanel>

          <BluePanel className="p-6 md:p-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/25 bg-amber-400/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-amber-300">
              <AlertTriangle className="h-4 w-4" />
              Before You Download
            </div>

            <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-white md:text-3xl">
              Read this first so nothing feels sketchy
            </h2>

            <div className="mt-5 grid gap-3">
              <div className="rounded-2xl border border-amber-400/20 bg-amber-400/10 px-4 py-4">
                <div className="flex items-start gap-3">
                  <FileWarning className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
                  <div>
                    <p className="text-sm font-bold text-white">You may see “Windows protected your PC.”</p>
                    <p className="mt-1 text-sm leading-6 text-dtf-text-muted">
                      That does not automatically mean the file is bad. It usually means Windows has not seen enough downloads of this exact app yet.
                    </p>
                  </div>
                </div>
              </div>

              <InfoRow>Click “More info” only if you trust the source and downloaded it from your DTF Wizard account.</InfoRow>
              <InfoRow>Keep the downloaded file name unchanged so you know it came from your official download.</InfoRow>
              <InfoRow>Contact support if Windows blocks it, your antivirus removes it, or you are unsure.</InfoRow>
            </div>

            <div className="mt-5 rounded-2xl border border-dtf-blue/20 bg-dtf-blue/10 p-4">
              <p className="text-sm font-bold text-white">Need help?</p>
              <p className="mt-1 text-sm leading-6 text-dtf-text-muted">
                Email support at{" "}
                <a className="font-semibold text-dtf-blue underline" href={supportMailto}>
                  {SUPPORT_EMAIL}
                </a>
                .
              </p>
            </div>
          </BluePanel>
        </section>
      </div>
    </div>
  );
}